import React, { useState } from 'react';
import IngredientAutocomplete from './IngredientAutocomplete';
import UnitAutocomplete, { Unit } from './UnitAutocomplete';
import { Button } from './../ui/atoms/Button/Button';

export type Ingredient = {
  id: string;
  name: string;
  catalogId: string | null;
  quantity: string;
  unitId: string;
  unitLabel?: string;
  scalable?: boolean;
  isVerified: boolean;
};

type Props = {
  ingredient: Ingredient;
  units: Unit[];
  onChange: (ing: Ingredient) => void;
  onRemove: () => void;
};

export default function IngredientRow({
  ingredient,
  units,
  onChange,
  onRemove,
}: Props) {
  const [touched, setTouched] = useState(!ingredient.isVerified);
  const isUnverified =
    touched &&
    ingredient.name.trim() !== '' &&
    !ingredient.isVerified;
  const handleNameSelect = (sel: {
    name: string;
    catalogId: string | null;
  }) => {
    onChange({ 
      ...ingredient, 
      name: sel.name, 
      catalogId: sel.catalogId,
      isVerified: true, 
      unitId: '', 
      unitLabel: '',
    });
    setTouched(true);
  };

  // ← this new handler fires on every keystroke
  const handleNameChange = (newName: string) => {
     onChange({
      ...ingredient,
      name: newName,
      catalogId: null,
      isVerified: false,
    });
  };

  const handleUnitSelect = ({
    label,
    unitId,
  }: {
    label: string;
    unitId: string;
  }) => {
    onChange({ ...ingredient, unitId, unitLabel: label });
  };

  // derive what to show in the input if they already had a unitId
  const displayUnit =
    ingredient.unitLabel ||
    units.find((u) => u.id === ingredient.unitId)?.label ||
    '';

  return (
    <div
      className={`w-full pl-2 py-1 rounded ${
        isUnverified ? 'bg-red-100' : 'bg-white'
      }`}
    >
      <div className="grid grid-cols-12 gap-2 items-center">
        {/* Ingredient name */}
        <div className="col-span-5">
          <IngredientAutocomplete
            value={ingredient.name}
            onSelect={handleNameSelect}
            onInputChange={handleNameChange}   // ← capture typing!
            placeholder="Ingredient"
            onBlur={() => setTouched(true)}
          />
        </div>

        {/* Quantity */}
        <input
          type="text"
          className="col-span-2 px-2 py-1 rounded w-full border border-gray-300 focus:border-black focus:outline-none"
          placeholder="Qty"
          value={ingredient.quantity}
          onChange={(e) =>
            onChange({ ...ingredient, quantity: e.target.value })
          }
        />

        {/* Unit autocomplete */}
        <div className="col-span-4">
          <UnitAutocomplete
            units={units}
            value={displayUnit}
            onSelect={handleUnitSelect}
            placeholder="i.e. Piece"
          />
        </div>
        <Button variant="remove" onClick={onRemove} />
      </div>
    </div>
  );
}
